import "@/sentry"; // KX-71: Sentry must init before all other code
import "./intercom";
import { useState, useEffect } from "react";
import "./global.css";
import { type ReactNode } from "react";
import i18n from "@/i18n";
import { Toaster } from "@/components/ui/toaster";
import { createRoot } from "react-dom/client";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "@/contexts/AppContext";
import { buildLoginUrl } from "@/api/auth/oidcConfig";
import { HelmetProvider } from "react-helmet-async";
import { I18nextProvider } from "react-i18next";
import { Sentry } from "@/sentry";

// Authentication Imports

import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import InvitationRegister from "./pages/InvitationRegister";
import VerifyEmail from "./pages/VerifyEmail";
import Page404 from "./pages/NotFound";
import Verify2FA from "./pages/Verify2FA";
// ADMIN DASHBOARD IMPORTS
import AdminLayout from "./components/layouts/AdminLayout";
import AdminOverview from "./pages/admin/Overview";
import AdminUsers from "./pages/admin/Users";
import AdminHotspot from "./pages/admin/Hotspots";
import AdminPayoutDashboard from "./pages/admin/Payout";
import AdminFiatPayouts from "./pages/admin/FiatPayouts";
import AdminReferrals from "./pages/admin/Referrals";
import FundDistribution from "./pages/admin/FundDistribution";
import Organisation from "./pages/admin/Organisation";
import CountryManager from "./pages/admin/CountryManager";
import AdminSupport from "./pages/admin/Support";
import AdminHotspotRequests from "./pages/admin/AdminHotspotRequests";
import AdminPlans from "./pages/admin/Plans";
import AdminKits from "./pages/admin/Kits";
import AdminKitTiers from "./pages/admin/KitTiers";
import AdminProducts from "./pages/admin/Products";
import AdminOrders from "./pages/admin/Orders";
import AdminCurrencyCountries from "./pages/admin/CurrencyCountries";
import AdminRateCards from "./pages/admin/RateCards";
import TeamAdminAccounts from "./pages/admin/TeamAdminAccounts";
import TaxProfiles from "./pages/admin/TaxProfiles";
import ActivationFees from "./pages/admin/ActivationFees";
import AcceptAdminInvite from "./pages/admin/AcceptAdminInvite";

// EndUsers Dashboard Imports
import EndUserDashboardLayout from "./components/layouts/EndUsersDashboardlayout";
import KonnectXEndusersDashboard from "./pages/EndUsersDashboard/Dashboardoverview";
import KonnectXEndUsersHotspots from "./pages/EndUsersDashboard/Hotspots";
import PricingPlans from "./pages/EndUsersDashboard/PricingPlan";
import SubscriptionsPage from "./pages/EndUsersDashboard/Subscription";
import WalletDashboard from "./pages/EndUsersDashboard/Wallet";
import ProfileSettings from "./pages/EndUsersDashboard/Profile";
import NotificationPanel from "./pages/EndUsersDashboard/Notification";
import DonatePage from "./pages/EndUsersDashboard/Donate";
import SupportDonatePage from "./pages/EndUsersDashboard/SupportDonate";

// Operators Dashboard Imports
import DashboardLayout from "./components/layouts/OperatorDashboardLayout";
import DashboardOverview from "./pages/dashboard/Overview";
import ManageKits from "./pages/OperatorsDashboard/operatorkit";
import DashboardReferral from "./pages/OperatorsDashboard/Referrals";
import DashboardReward from "./pages/OperatorsDashboard/Reward";
import HotspotRegistration from "./pages/OperatorsDashboard/registerKit";
import HotspotRegistrationConnectivity from "./pages/OperatorsDashboard/registerKitConnectivity";
import KonnectXSetupGuide from "./pages/OperatorsDashboard/setupguide";
import HotspotStorePage from "./pages/OperatorsDashboard/Store";
import KonnectXToken from "./pages/OperatorsDashboard/Token";
import AccountSettings from "./pages/OperatorsDashboard/Settings";
import OperatorSupport from "./pages/OperatorsDashboard/Support";
import HotspotRegistrationStep2 from "./pages/OperatorsDashboard/registerKitStep2";
import HotspotRegistrationStep3 from "./pages/OperatorsDashboard/registerKitStep3";
import KonnectXKitss from "./pages/OperatorsDashboard/AllKits";
import IndependentOperators from "./pages/OperatorsDashboard/independentOperator";
import HotspotAcess from "./pages/OperatorsDashboard/HotspotAcess";
import MyHotSpots from "./pages/OperatorsDashboard/MyHotspots";
import VoucherManagement from "./pages/OperatorsDashboard/Vouchers";
import FinanceDashboard from "./pages/OperatorsDashboard/Finances";
import WithdrawFunds from "./pages/OperatorsDashboard/Withdrawfunds";
import OperatorPayouts from "./pages/OperatorsDashboard/Payouts";
import ProductDetail from "./pages/OperatorsDashboard/ProductDetail";
import Cart from "./pages/OperatorsDashboard/Cart";
import Checkout from "./pages/OperatorsDashboard/Checkout";
import CheckoutVerify from "./pages/OperatorsDashboard/CheckoutVerify";
import Orders from "./pages/OperatorsDashboard/Orders";
import OrderDetail from "./pages/OperatorsDashboard/OrderDetail";
import AccountPage from "./pages/OperatorsDashboard/OperatorProfile";
import OperatorDonatePage from "./pages/OperatorsDashboard/Donate";
import OperatorSupportDonatePage from "./pages/OperatorsDashboard/SupportDonate";
import WalletPaymentCallback from "./pages/WalletPaymentCallback";
import AuthCallback from "./pages/AuthCallback";
import PlanVerify from "./pages/PlanVerify";
import Wishlist from "./pages/OperatorsDashboard/wishlist";
import LaunchCountdownAdmin from "@/components/LaunchCountdownAdmin";


const queryClient = new QueryClient();
// ==========================================
// ROUTE PROTECTION LOGIC - SIMPLE
// ==========================================

const ADMIN_ROLES = ['admin', 'super-admin'];

/** Safely read the cached user's role from localStorage (null if absent/invalid). */
const readCachedRole = (): string | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr)?.role ?? null;
  } catch {
    return null;
  }
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const userStr = localStorage.getItem('user');
  
  if (!userStr) {
    return <Navigate to="/kxt-mgmt/login" replace />;
  }
  
  try {
    const user = JSON.parse(userStr);

    // Admin panel is for admins AND super-admins.
    if (!['admin', 'super-admin'].includes(user.role)) {
      if (user.role === 'operator') return <Navigate to="/operator" replace />;
      if (user.role === 'end-user') return <Navigate to="/user" replace />;
      return <Navigate to="/kxt-mgmt/login" replace />;
    }
    
    return <>{children}</>;
  } catch {
    return <Navigate to="/kxt-mgmt/login" replace />;
  }
  return <>{children}</>;
};

const OperatorRoute = ({ children }: { children: React.ReactNode }) => {
  const role = readCachedRole();

  if (!role) return <Navigate to="/signin" replace />;
  if (!['operator', 'admin', 'super-admin'].includes(role)) {
    return <Navigate to="/user" replace />;
  }
  return <>{children}</>;
};

const UserRoute = ({ children }: { children: React.ReactNode }) => {
  const role = readCachedRole();

  if (!role) return <Navigate to="/signin" replace />;
  if (role === 'operator') return <Navigate to="/operator" replace />;
  if (role === 'admin' || role === 'super-admin') return <Navigate to="/kxt-mgmt" replace />;
  return <>{children}</>;
};

// Provider-portable login entrypoints. These redirect to our BFF's OIDC login
// (Authorization Code + PKCE); the provider is config-driven (no vendor literal).
const ExternalLogin = () => {
  window.location.href = buildLoginUrl();
  return null;
};

const ExternalAdminLogin = () => {
  window.location.href = buildLoginUrl({ role: 'admin' });
  return null;
};

/** Fallback UI shown when an unhandled error reaches the Sentry ErrorBoundary. */
const SentryFallback = () => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", minHeight: "100vh", fontFamily: "system-ui, sans-serif",
    padding: "2rem", textAlign: "center",
  }}>
    <h1 style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>Something went wrong</h1>
    <p style={{ color: "#666", marginBottom: "1.5rem" }}>
      An unexpected error occurred. Please try reloading the page.
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: "0.75rem 1.5rem", borderRadius: "0.5rem",
        border: "none", background: "#2563eb", color: "white",
        cursor: "pointer", fontSize: "1rem",
      }}
    >
      Reload Page
    </button>
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={<SentryFallback />}>
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <AppProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              
<Routes>
  
  <Route path="/wallet/payment/callback" element={<WalletPaymentCallback />} />
  <Route path="/plans/verify" element={<PlanVerify />} />
  <Route path="/app/plans/topup/verify" element={<PlanVerify />} />
  <Route path="/app/plans/subscribe/verify" element={<PlanVerify />} />
  <Route path="/app/donate/verify" element={<WalletPaymentCallback />} />
  
  <Route path="/" element={<Navigate to="/signin" replace />} />
  
  <Route path="/signin" element={<ExternalLogin />} />
  <Route path="/verify-2fa" element={<Verify2FA />} />
  <Route path="/operator/verify-2fa" element={<Verify2FA />} />
  <Route path="/register" element={<Register />} />

  {/* ⭐ CRITICAL: Auth callback MUST be public and come BEFORE protected routes */}
  <Route path="/auth/callback" element={<AuthCallback />} />

  {/* Email Verification Route */}
  <Route path="/verify-email" element={<VerifyEmail />} />
  <Route path="/forgot-password" element={<ForgotPassword />} />
  <Route path="/reset-password" element={<ResetPassword />} />
  <Route path="/invitation/register" element={<InvitationRegister />} />
  <Route path="/admin/accept-invite" element={<AcceptAdminInvite />} />

  {/* End Users Dashboard - Protected by UserRoute */}
  <Route
    path="/user"
    element={
      <UserRoute>
        <EndUserDashboardLayout />
      </UserRoute>
    }
  >
    <Route index element={<KonnectXEndusersDashboard />} />
    <Route path="hotspots" element={<KonnectXEndUsersHotspots />} />
    <Route path="subscription" element={<SubscriptionsPage />} />
    <Route path="wallet" element={<WalletDashboard />} />
    <Route path="pricing" element={<PricingPlans />} />
    <Route path="notifications" element={<NotificationPanel />} />
    <Route path="profile" element={<ProfileSettings />} />
    <Route path="donate" element={<DonatePage />} />
    <Route path="support-donate" element={<SupportDonatePage />} />
    <Route path="wallet/payment/callback" element={<WalletPaymentCallback />} />
  </Route>

  {/* Legacy Redirects */}
  <Route path="/Dashboardoverview" element={<Navigate to="/user" replace />} />
  <Route path="/Hotspots" element={<Navigate to="/user/hotspots" replace />} />
  <Route path="/Subscription" element={<Navigate to="/user/subscription" replace />} />
  <Route path="/Wallet" element={<Navigate to="/user/wallet" replace />} />
  <Route path="/PricingPlan" element={<Navigate to="/user/pricing" replace />} />
  <Route path="/Notification" element={<Navigate to="/user/notifications" replace />} />
  <Route path="/Donate" element={<Navigate to="/user/donate" replace />} />
  <Route path="/SupportDonate" element={<Navigate to="/user/support-donate" replace />} />
  <Route path="/store" element={<Navigate to="/operator/store" replace />} />
  <Route path="/Store" element={<Navigate to="/operator/store" replace />} />
  <Route path="/cart" element={<Navigate to="/operator/cart" replace />} />

  {/* Operator Dashboard - Protected by OperatorRoute */}
  <Route
    path="/operator"
    element={
      <OperatorRoute>
        <DashboardLayout />
      </OperatorRoute>
    }
  >
    <Route index element={<DashboardOverview />} />
    <Route path="hotspots" element={<DashboardOverview />} />
    <Route path="reward" element={<DashboardReward />} />
    <Route path="Referrals" element={<DashboardReferral />} />
    <Route path="setupguide" element={<KonnectXSetupGuide />} />
    <Route path="store" element={<HotspotStorePage />} />
    <Route path="Store" element={<Navigate to="/operator/store" replace />} />
    <Route path="store/product/:id" element={<ProductDetail />} />
    <Route path="cart" element={<Cart />} />
    <Route path="checkout" element={<Checkout />} />
    <Route path="checkout/verify" element={<CheckoutVerify />} />
    <Route path="orders" element={<Orders />} />
    <Route path="orders/:id" element={<OrderDetail />} />
    <Route path="Settings" element={<AccountSettings />} />
    <Route path="Token" element={<KonnectXToken />} />
    <Route path="operatorkit" element={<ManageKits />} />
    <Route path="registerKit" element={<HotspotRegistration />} />
    <Route path="registerKitConnectivity" element={<HotspotRegistrationConnectivity />} />
    <Route path="registerKitStep2" element={<HotspotRegistrationStep2 />} />
    <Route path="registerKitStep3" element={<HotspotRegistrationStep3 />} />
    <Route path="Allkits" element={<KonnectXKitss />} />
    <Route path="independentOperator" element={<IndependentOperators />} />
    <Route path="HotspotAcess" element={<HotspotAcess />} />
    <Route path="MyHotspots" element={<MyHotSpots />} />
    <Route path="Vouchers" element={<VoucherManagement />} />
    <Route path="Finances" element={<FinanceDashboard />} />
    <Route path="Withdrawfunds" element={<WithdrawFunds />} />
    <Route path="payouts" element={<OperatorPayouts />} />
    <Route path="donate" element={<OperatorDonatePage />} />
    <Route path="support-donate" element={<OperatorSupportDonatePage />} />
    <Route path="support" element={<OperatorSupport />} />
    <Route path="profile" element={<AccountPage />} />
     <Route path="wishlist" element={<Wishlist />} />
  </Route>

  {/* Admin Dashboard */}
  <Route
    path="/kxt-mgmt"
    element={
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    }
  >
    <Route index element={<AdminOverview />} />
    <Route path="launch" element={<LaunchCountdownAdmin />} />
    <Route path="user-management" element={<AdminUsers />} />
    <Route path="hotspots" element={<AdminHotspot />} />
    <Route path="referrals" element={<AdminReferrals />} />
    <Route path="payouts" element={<AdminPayoutDashboard />} />
    <Route path="fiat-payouts" element={<AdminFiatPayouts />} />
    <Route path="fund-distribution" element={<FundDistribution />} />
    <Route path="organisation" element={<Organisation />} />
    <Route path="country-manager" element={<CountryManager />} />
    <Route path="support" element={<AdminSupport />} />
    <Route path="coverage-demand" element={<AdminHotspotRequests />} />
    <Route path="plans" element={<AdminPlans />} />
    <Route path="tax-profiles" element={<TaxProfiles />} />
    <Route path="activation-fees" element={<ActivationFees />} />
    <Route path="kits" element={<AdminKits />} />
    <Route path="kit-tiers" element={<AdminKitTiers />} />
    <Route path="products" element={<AdminProducts />} />
    <Route path="orders" element={<AdminOrders />} />
    <Route path="currency-markets" element={<AdminCurrencyCountries />} />
    <Route path="rate-cards" element={<AdminRateCards />} />
    <Route path="team" element={<TeamAdminAccounts />} />
  </Route>

  <Route path="/kxt-mgmt/login" element={<ExternalAdminLogin />} />

  {/* 404 */}
  <Route path="*" element={<Page404 />} />
</Routes>
              </BrowserRouter>
            </TooltipProvider>
        </AppProvider>
      </I18nextProvider>
    </QueryClientProvider>
  </HelmetProvider>
  </Sentry.ErrorBoundary>
);

createRoot(document.getElementById("root")!).render(<App />);
export default App;